import { useEffect, useState } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { useUpdateCrossBorderReceiver } from '../../fetchHooks/useCrossBorderReceivers';
import type { CrossBorderReceiver } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import ReceiverAction from './ReceiverActionType';
import createUpdateCrossBorderReceiverRequest from './createUpdateCrossBorderReceiverRequest';
import validateForm, { ValidationMessage } from './validateForm';

const initialValidationErrors = {};

const useUpdateReceiver = (
    receiver: CrossBorderReceiver,
    onEdit: () => void,
    setReceiverAction: (action: ReceiverAction) => void,
) => {
    const translated = useTranslation();
    const [receiverName, setReceiverName] = useState(receiver.name);
    const [receiverAddress, setReceiverAddress] = useState(receiver.address || '');
    const [receiverAddress2, setReceiverAddress2] = useState(receiver.address2 || '');
    const [receiverPostCode, setReceiverPostCode] = useState(receiver.postcode || '');
    const [receiverTown, setReceiverTown] = useState(receiver.town || '');
    const [receiverCountryName, setReceiverCountryName] = useState<string>();
    const [receiverCountryCode, setReceiverCountryCode] = useState(receiver.countryCode);
    const [updateReceiverError, setUpdateReceiverError] = useState<RequestExceptionWithResponse>();
    const [updateReceiverIsLoading, setUpdateReceiverIsLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState<ValidationMessage>(initialValidationErrors);

    const update = useUpdateCrossBorderReceiver(setUpdateReceiverIsLoading, setUpdateReceiverError);

    useEffect(() => {
        setValidationErrors(initialValidationErrors);
    }, [receiverName, receiverAddress, receiverCountryName]);

    const hasStateChange =
        receiverName !== receiver.name ||
        receiverAddress !== (receiver.address || '') ||
        receiverAddress2 !== (receiver.address2 || '') ||
        receiverPostCode !== (receiver.postcode || '') ||
        receiverTown !== (receiver.town || '') ||
        receiverCountryCode !== receiver.countryCode;

    const updateReceiver = (event: { preventDefault: () => void }) => {
        event.preventDefault();

        const errors = validateForm({
            receiverName,
            country: receiverCountryName,
            translated,
            requiredAddress: Boolean(receiver.address),
            address: receiverAddress,
            requiredPostCode: Boolean(receiver.postcode),
            postCode: receiverPostCode,
            requiredTown: Boolean(receiver.town),
            town: receiverTown,
        });

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        const updateReceiver = createUpdateCrossBorderReceiverRequest(
            receiver,
            receiverAddress,
            receiverAddress2,
            receiverPostCode,
            receiverTown,
            receiverName,
            receiverCountryCode,
        );

        if (hasStateChange && receiver.id) {
            setReceiverAction(ReceiverAction.EDIT);
            setUpdateReceiverIsLoading(true);
            update.mutate({ receiverId: receiver.id, ...updateReceiver });
            onEdit();
        }
    };

    return {
        receiverName: {
            value: receiverName,
            set: setReceiverName,
        },
        receiverAddress: {
            value: receiverAddress,
            set: setReceiverAddress,
        },
        receiverAddress2: {
            value: receiverAddress2,
            set: setReceiverAddress2,
        },
        receiverPostCode: {
            value: receiverPostCode,
            set: setReceiverPostCode,
        },
        receiverTown: {
            value: receiverTown,
            set: setReceiverTown,
        },
        receiverCountryName: {
            value: receiverCountryName,
            set: setReceiverCountryName,
        },
        receiverCountryCode: {
            value: receiverCountryCode,
            set: setReceiverCountryCode,
        },
        update: {
            error: {
                value: updateReceiverError,
                set: setUpdateReceiverError,
            },
            loading: {
                value: updateReceiverIsLoading,
                set: setUpdateReceiverIsLoading,
            },
            validation: validationErrors,
            mutate: updateReceiver,
        },
        hasStateChange,
    };
};
export default useUpdateReceiver;
